/**
 * METRICS REGISTRY — single source of truth for camera-view eligibility
 * and per-metric measurement priority.
 *
 * Each metric carries an ORDERED list of camera views that can reliably
 * measure it. When a session captures the same metric from multiple views
 * (e.g. elbow visible from both Side and Angled), the display picks the
 * value from the FIRST eligible view that produced data — so we always
 * use the most accurate measurement, never an average.
 *
 * View-priority rationale:
 *   - Side    → cleanest sagittal-plane (flexion/extension) angles.
 *               No depth ambiguity for hinge joints.
 *   - Angled  → ~30-60° oblique. Joint angles are still measurable but
 *               with ~10-20% more noise than pure side. Acceptable
 *               fallback when no side data is captured.
 *   - Front   → only useful for lateral comparisons (left vs right
 *               symmetry, valgus, tilt). Cannot measure flexion at all.
 *
 * Every metric label below MUST match the label that the live pose
 * pipeline emits AND the label that backend stores in joint_angles.
 * This is the contract — both sides import from this file (frontend) /
 * its python mirror (backend).
 */

const N = (s) => String(s || '').toLowerCase().trim().replace(/[_-]/g, ' ').replace(/\s+/g, ' ');

// Note: keys here are normalized labels. Matching uses N(label) so the
// registry tolerates "Knee", "knee", "Left Knee", "L_Knee", "Knees" all
// landing on the same row (we strip the "Left/Right/L/R" prefix below).

const stripSidePrefix = (label) => {
  const n = N(label);
  // "L knee", "left knee" → "knee"; "R hip", "right hip" → "hip"
  return n.replace(/^(l|r|left|right)\s+/, '');
};

// ────────────────────────────────────────────────────────────────────────
// Registry — eligible_views ordered by accuracy (best → fallback).
// Empty list means "any view OK". `phase` is what part of the rep this
// metric represents in the display.
// ────────────────────────────────────────────────────────────────────────

const REGISTRY = {
  // Sagittal-plane flexion/extension (need a side view; angled is a fallback).
  knee:                 { eligible_views: ['side', 'angled'], display: 'Knee Angle',         phase: 'at_bottom'  },
  hip:                  { eligible_views: ['side', 'angled'], display: 'Hip Angle',          phase: 'at_bottom'  },
  elbow:                { eligible_views: ['side', 'angled'], display: 'Elbow Angle',        phase: 'at_bottom'  },
  shoulder:             { eligible_views: ['side', 'angled'], display: 'Shoulder Angle',     phase: 'throughout' },
  back:                 { eligible_views: ['side', 'angled'], display: 'Back Angle',         phase: 'throughout' },
  ankle:                { eligible_views: ['side'],            display: 'Ankle Angle',        phase: 'at_bottom'  },
  neck:                 { eligible_views: ['side'],            display: 'Neck Angle',         phase: 'throughout' },
  wrist:                { eligible_views: ['side'],            display: 'Wrist Angle',        phase: 'throughout' },
  spine:                { eligible_views: ['side', 'angled'], display: 'Spine Angle',        phase: 'throughout' },
  torso:                { eligible_views: ['side', 'angled'], display: 'Torso Angle',        phase: 'throughout' },
  'hip line':           { eligible_views: ['side', 'angled'], display: 'Hip Line',           phase: 'throughout' },
  'elbow bend':         { eligible_views: ['side', 'angled'], display: 'Elbow Bend',         phase: 'at_bottom'  },
  'knee bend':          { eligible_views: ['side', 'angled'], display: 'Knee Bend',          phase: 'at_bottom'  },
  'back lean':          { eligible_views: ['side', 'angled'], display: 'Back Lean',          phase: 'throughout' },

  // Lateral / symmetry — only measurable from front.
  'shoulder symmetry':  { eligible_views: ['front'], display: 'Shoulder Symmetry',  phase: 'throughout' },
  'hip symmetry':       { eligible_views: ['front'], display: 'Hip Symmetry',       phase: 'throughout' },
  'knee valgus':        { eligible_views: ['front'], display: 'Knee Valgus',        phase: 'at_bottom'  },
  'elbow flare':        { eligible_views: ['front'], display: 'Elbow Flare',        phase: 'at_bottom'  },
  'shoulder tilt':      { eligible_views: ['front'], display: 'Shoulder Tilt',      phase: 'throughout' },
  'foot stance width':  { eligible_views: ['front'], display: 'Foot Stance Width',  phase: 'throughout' },

  // Visible from either plane.
  'head alignment':     { eligible_views: ['side', 'front'], display: 'Head Alignment', phase: 'throughout' },
};

// ────────────────────────────────────────────────────────────────────────
// Public helpers
// ────────────────────────────────────────────────────────────────────────

/** Lookup the registry row for a label. Tolerates L/R prefixes. */
export const lookupMetric = (label) => {
  if (!label) return null;
  const key = stripSidePrefix(label);
  if (REGISTRY[key]) return { key, ...REGISTRY[key] };
  // Try the normalized full label too (e.g. "shoulder symmetry")
  const full = N(label);
  if (REGISTRY[full]) return { key: full, ...REGISTRY[full] };
  return null;
};

/**
 * Is this metric reliable from `cameraView`?
 * Unknown / null view → permissive (returns true).
 * Unknown metric → permissive (returns true) so user-defined / Claude-defined
 * checkpoints not in the registry still flow through.
 */
export const isEligibleForView = (label, cameraView) => {
  const v = N(cameraView);
  if (!v || v === 'unknown') return true;
  const m = lookupMetric(label);
  if (!m) return true;       // unknown metric → don't filter (legacy / future-proof)
  return m.eligible_views.includes(v);
};

/**
 * Given a metric label and a list of {view, value} samples gathered across a
 * session, return the BEST sample — the one whose view has highest priority
 * in the registry. Falls back to median across same-view samples when there
 * are multiple at the same priority. Never averages across different views.
 *
 * Returns null when no eligible sample exists.
 */
export const pickBestSample = (label, samples) => {
  if (!Array.isArray(samples) || samples.length === 0) return null;
  const m = lookupMetric(label);
  // Filter to eligible-view samples only.
  const eligible = samples.filter(s => {
    if (!m) return true;     // unknown metric: keep all
    return m.eligible_views.includes(N(s.view));
  });
  if (eligible.length === 0) return null;

  // Group by view; pick the view group with highest priority (lowest index in
  // eligible_views). If the metric is unknown, just take the first view group.
  const priority = (view) => {
    if (!m) return 0;
    const i = m.eligible_views.indexOf(N(view));
    return i === -1 ? 999 : i;
  };
  eligible.sort((a, b) => priority(a.view) - priority(b.view));
  const bestView = eligible[0].view;
  const sameView = eligible.filter(s => N(s.view) === N(bestView));
  // Median within that view (no cross-view averaging).
  const vals = sameView
    .map(s => Number(s.value))
    .filter(v => Number.isFinite(v))
    .sort((a, b) => a - b);
  if (vals.length === 0) return null;
  const median = vals[Math.floor(vals.length / 2)];
  return { value: median, view: bestView, sampleCount: sameView.length };
};

/** Display label (Title Case) for a metric label. Used in headers. */
export const displayLabel = (label) => {
  const m = lookupMetric(label);
  if (m?.display) return m.display;
  // Title-case the raw label as fallback
  return String(label || '').replace(/(^|\s)\w/g, c => c.toUpperCase());
};

/** All registered metric keys (lowercase). */
export const ALL_METRIC_KEYS = Object.keys(REGISTRY);

/** What hint to show the user when a metric had ZERO eligible samples. */
export const hintForMissingView = (label) => {
  const m = lookupMetric(label);
  if (!m) return null;
  const v = m.eligible_views[0];
  return `Film one set from the ${v} next time to unlock ${m.display}.`;
};

export default {
  lookupMetric,
  isEligibleForView,
  pickBestSample,
  displayLabel,
  ALL_METRIC_KEYS,
  hintForMissingView,
};
